import NotificationsPausedOutlinedIcon from '@mui/icons-material/NotificationsPausedOutlined'
import { Badge, Button, Menu, MenuList, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useAuthStore } from '../hooks/useAuthStore'
import { useMarkAllNotificationsAsReadMutation } from '../hooks/useMutations/useNotificationsMutations'
import { useNotifications } from '../hooks/useQueries/useUsers'
import { LoaderButton } from './LoaderButton'
import NotificationMenuItem from './NotificationMenuItem'

export default function NotificationsMenu({
  anchorEl,
  isOpen,
  handleCloseMenu
}: {
  anchorEl: null | HTMLElement
  isOpen: boolean
  handleCloseMenu: () => void
}) {
  const { t } = useTranslation()
  const { userId } = useAuthStore() as { userId: string }
  const queryParams = {
    pageSize: '6',
    pageNumber: '1'
  }
  const { data: notificationsPaginator } = useNotifications(userId, queryParams)
  const markAllNotificationsAsReadMutation =
    useMarkAllNotificationsAsReadMutation(userId)

  return (
    <Menu
      anchorEl={anchorEl}
      open={isOpen}
      onClose={handleCloseMenu}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center'
      }}
      slotProps={{
        paper: {
          sx: {
            width: '36rem',
            borderRadius: '0.5rem'
          },
          elevation: 2
        }
      }}
    >
      <Stack direction='row' justifyContent='space-between' padding='1.5rem'>
        <Stack direction='row' spacing='1rem' alignItems='center'>
          <Typography variant='h6' fontWeight='bold'>
            {t('notifications.label')}
          </Typography>
          {notificationsPaginator?.totalUnread &&
          notificationsPaginator?.totalUnread > 0 ? (
            <Badge
              color='primary'
              badgeContent={notificationsPaginator?.totalUnread}
            />
          ) : (
            <Badge color='primary' badgeContent={0} showZero />
          )}
        </Stack>
        <LoaderButton
          variant='text'
          size='large'
          label={t('notifications_menu.mark_all_as_read')}
          onClick={() => markAllNotificationsAsReadMutation.mutate(userId)}
          disabled={markAllNotificationsAsReadMutation.isPending}
          loading={markAllNotificationsAsReadMutation.isPending}
          color='secondary'
          sx={{ textTransform: 'none', fontWeight: 600 }}
          disableEndIcon={true}
        />
      </Stack>

      {notificationsPaginator?.notifications.length ? (
        <MenuList disablePadding>
          {notificationsPaginator?.notifications?.map(notification => (
            <NotificationMenuItem
              key={notification.id}
              notification={notification}
              handleCloseMenu={handleCloseMenu}
            />
          ))}
        </MenuList>
      ) : (
        [
          <Stack
            key='empty-state'
            direction='row'
            spacing='1rem'
            padding='1rem'
          >
            <NotificationsPausedOutlinedIcon sx={{ color: 'text.secondary' }} />
            <Stack direction='column' spacing='.25rem'>
              <Typography variant='body1'>
                {t('notifications_menu.empty.title')}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {t('notifications_menu.empty.description')}
              </Typography>
            </Stack>
          </Stack>
        ]
      )}
      <Stack padding='1.25rem 1rem' alignItems='center'>
        <Button
          component={Link}
          to='/notifications'
          variant='text'
          onClick={handleCloseMenu}
          sx={{
            textTransform: 'none'
          }}
        >
          <Typography variant='body1'>
            {t('notifications_menu.go_to_notifications')}
          </Typography>
        </Button>
      </Stack>
    </Menu>
  )
}
